import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Mail, Webhook, Check, X, AlertCircle, 
  Copy, RefreshCw, Send, Plus, Edit, Trash2, Play,
  Eye, EyeOff, Settings, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useToast } from '../../hooks/use-toast';

const IntegrationsTab = () => {
  const [activeSection, setActiveSection] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  // Stripe states
  const [stripeConfig, setStripeConfig] = useState(null);
  const [stripeMetrics, setStripeMetrics] = useState(null);
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [showStripeKeys, setShowStripeKeys] = useState(false);
  const [stripeForm, setStripeForm] = useState({
    api_key: '',
    publishable_key: '',
    webhook_secret: ''
  });
  
  // SendGrid states
  const [sendgridConfig, setSendgridConfig] = useState(null);
  const [sendgridMetrics, setSendgridMetrics] = useState(null);
  const [sendgridConnecting, setSendgridConnecting] = useState(false);
  const [showSendgridKey, setShowSendgridKey] = useState(false);
  const [sendgridForm, setSendgridForm] = useState({
    api_key: '',
    sender_email: ''
  });
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  
  // Webhooks states
  const [webhooks, setWebhooks] = useState([]);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    secret: '',
    events: [],
    headers: {},
    enabled: true
  });
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [showDeliveryLogs, setShowDeliveryLogs] = useState(false);
  
  const API_BASE_URL = '/api';
  const adminToken = localStorage.getItem('auth_token');
  
  useEffect(() => {
    if (activeSection === 'stripe') {
      fetchStripeConfig();
    } else if (activeSection === 'sendgrid') {
      fetchSendgridConfig();
    } else if (activeSection === 'webhooks') {
      fetchWebhooks();
      fetchAvailableEvents();
    }
  }, [activeSection]);
  
  // ==================== STRIPE ====================
  
  const fetchStripeConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/stripe/config`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success && data.connected) {
        setStripeConfig(data.config);
        fetchStripeMetrics();
      }
    } catch (error) {
      console.error('Error fetching Stripe config:', error);
    }
  };
  
  const fetchStripeMetrics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/stripe/metrics`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setStripeMetrics(data.metrics);
      }
    } catch (error) {
      console.error('Error fetching Stripe metrics:', error);
    }
  };
  
  const connectStripe = async () => {
    if (!stripeForm.api_key || !stripeForm.publishable_key) {
      toast({
        title: "Missing Fields",
        description: "Please provide both API key and publishable key",
        variant: "destructive"
      });
      return;
    }
    
    setStripeConnecting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/stripe/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(stripeForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Success",
          description: `Stripe connected in ${data.mode} mode`,
        });
        
        setStripeForm({ api_key: '', publishable_key: '', webhook_secret: '' });
        fetchStripeConfig();
      } else {
        throw new Error(data.error || 'Connection failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setStripeConnecting(false);
    }
  };
  
  const testStripe = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/stripe/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Connection Test Successful",
          description: `Connected to Stripe account: ${data.account_name || data.account_id}`,
        });
      } else {
        throw new Error(data.error || 'Test failed');
      }
    } catch (error) {
      toast({
        title: "Connection Test Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const syncStripeData = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/stripe/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Sync Complete",
          description: "Stripe data synced successfully",
        });
        fetchStripeMetrics();
      } else {
        throw new Error(data.error || 'Sync failed');
      }
    } catch (error) {
      toast({
        title: "Sync Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const disconnectStripe = async () => {
    if (!confirm('Are you sure you want to disconnect Stripe? This will remove the stored API keys from the database.')) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/stripe/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Disconnected",
          description: "Stripe integration disconnected successfully",
        });
        
        setStripeConfig(null);
        setStripeMetrics(null);
        fetchStripeConfig(); // Will fall back to env
      } else {
        throw new Error(data.error || 'Disconnect failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== SENDGRID ====================
  
  const fetchSendgridConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/sendgrid/config`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success && data.connected) {
        setSendgridConfig(data.config);
        fetchSendgridMetrics();
      }
    } catch (error) {
      console.error('Error fetching SendGrid config:', error);
    }
  };
  
  const fetchSendgridMetrics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/sendgrid/metrics`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setSendgridMetrics(data.metrics);
      }
    } catch (error) {
      console.error('Error fetching SendGrid metrics:', error);
    }
  };
  
  const connectSendgrid = async () => {
    if (!sendgridForm.api_key || !sendgridForm.sender_email) {
      toast({
        title: "Missing Fields",
        description: "Please provide both API key and sender email",
        variant: "destructive"
      });
      return;
    }
    
    setSendgridConnecting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/sendgrid/connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sendgridForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Success",
          description: "SendGrid connected successfully",
        });
        
        setSendgridForm({ api_key: '', sender_email: '' });
        fetchSendgridConfig();
      } else {
        throw new Error(data.error || 'Connection failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSendgridConnecting(false);
    }
  };
  
  const testSendgrid = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/sendgrid/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Connection Test Successful",
          description: data.message || "SendGrid API key is valid",
        });
      } else {
        throw new Error(data.error || 'Test failed');
      }
    } catch (error) {
      toast({
        title: "Connection Test Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const sendTestEmail = async () => {
    if (!testEmailRecipient) {
      toast({
        title: "Missing Recipient",
        description: "Please enter a recipient email address",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/sendgrid/test-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ recipient_email: testEmailRecipient })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Test Email Sent",
          description: data.message,
        });
        setTestEmailRecipient('');
      } else {
        throw new Error(data.error || 'Failed to send');
      }
    } catch (error) {
      toast({
        title: "Failed to Send",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const disconnectSendgrid = async () => {
    if (!confirm('Are you sure you want to disconnect SendGrid? This will remove the stored API key from the database.')) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/sendgrid/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Disconnected",
          description: "SendGrid integration disconnected successfully",
        });
        
        setSendgridConfig(null);
        setSendgridMetrics(null);
        fetchSendgridConfig(); // Will fall back to env
      } else {
        throw new Error(data.error || 'Disconnect failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // ==================== WEBHOOKS ====================
  
  const fetchWebhooks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/webhooks`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setWebhooks(data.webhooks);
      }
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    }
  };
  
  const fetchAvailableEvents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/webhooks/events`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setAvailableEvents(data.events);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };
  
  const createOrUpdateWebhook = async () => {
    if (!webhookForm.name || !webhookForm.url || !webhookForm.secret || webhookForm.events.length === 0) {
      toast({
        title: "Missing Fields",
        description: "Please fill all required fields and select at least one event",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const url = editingWebhook 
        ? `${API_BASE_URL}/integrations/webhooks/${editingWebhook.id}`
        : `${API_BASE_URL}/integrations/webhooks`;
      
      const method = editingWebhook ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(webhookForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Success",
          description: `Webhook ${editingWebhook ? 'updated' : 'created'} successfully`,
        });
        
        setShowWebhookModal(false);
        setWebhookForm({
          name: '',
          url: '',
          secret: '',
          events: [],
          headers: {},
          enabled: true
        });
        setEditingWebhook(null);
        fetchWebhooks();
      } else {
        throw new Error(data.error || 'Operation failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const deleteWebhook = async (webhookId) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/integrations/webhooks/${webhookId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Deleted",
          description: "Webhook deleted successfully",
        });
        fetchWebhooks();
      } else {
        throw new Error(data.error || 'Delete failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  
  const fetchDeliveryLogs = async (webhookId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/integrations/webhooks/${webhookId}/deliveries?limit=50`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      const data = await response.json();
      
      if (data.success) {
        setDeliveryLogs(data.deliveries);
        setSelectedWebhook(webhookId);
        setShowDeliveryLogs(true);
      }
    } catch (error) {
      console.error('Error fetching delivery logs:', error);
    }
  };
  
  const retryDelivery = async (deliveryId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/integrations/webhooks/deliveries/${deliveryId}/retry`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Retry Complete",
          description: data.status_code < 400 ? "Delivery succeeded" : "Delivery failed",
        });
        
        if (selectedWebhook) {
          fetchDeliveryLogs(selectedWebhook);
        }
      } else {
        throw new Error(data.error || 'Retry failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  
  const toggleEventSelection = (eventId) => {
    setWebhookForm(prev => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId]
    }));
  };
  
  // ==================== RENDER ====================
  
  return (
    <div className="space-y-6" data-testid="integrations-tab">
      {/* Section Navigation */}
      <div className="inline-flex rounded-2xl p-1 backdrop-blur-xl"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.5) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.2)',
           }}>
        <button
          onClick={() => setActiveSection('stripe')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
            activeSection === 'stripe'
              ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow-lg'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="stripe-tab-btn"
        >
          <CreditCard className="w-5 h-5" />
          <span>Stripe</span>
        </button>
        <button
          onClick={() => setActiveSection('sendgrid')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
            activeSection === 'sendgrid'
              ? 'bg-gradient-to-r from-emerald-400 to-emerald-600 text-white shadow-lg'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="sendgrid-tab-btn"
        >
          <Mail className="w-5 h-5" />
          <span>SendGrid</span>
        </button>
        <button
          onClick={() => setActiveSection('webhooks')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
            activeSection === 'webhooks'
              ? 'bg-gradient-to-r from-purple-400 to-purple-600 text-white shadow-lg'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="webhooks-tab-btn"
        >
          <Webhook className="w-5 h-5" />
          <span>Webhooks</span>
        </button>
      </div>
      
      {/* Stripe Section */}
      {activeSection === 'stripe' && (
        <div className="space-y-6">
          <Card className="p-6" data-testid="stripe-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Stripe Integration</h3>
                  <p className="text-sm text-gray-500">Manage billing and subscriptions</p>
                </div>
              </div>
              
              {stripeConfig && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-50 text-green-700">
                    <Check className="w-4 h-4" />
                    Connected ({stripeConfig.mode})
                  </span>
                </div>
              )}
            </div>
            
            {!stripeConfig ? (
              <div className="space-y-4">
                <p className="text-gray-600">Connect your Stripe account to manage payments and subscriptions.</p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secret Key
                    </label>
                    <input
                      type={showStripeKeys ? "text" : "password"}
                      value={stripeForm.api_key}
                      onChange={(e) => setStripeForm({ ...stripeForm, api_key: e.target.value })}
                      placeholder="sk_live_... or sk_test_..."
                      className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                      data-testid="stripe-api-key-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Publishable Key
                    </label>
                    <input
                      type={showStripeKeys ? "text" : "password"}
                      value={stripeForm.publishable_key}
                      onChange={(e) => setStripeForm({ ...stripeForm, publishable_key: e.target.value })}
                      placeholder="pk_live_... or pk_test_..."
                      className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                      data-testid="stripe-pub-key-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Webhook Secret (Optional)
                    </label>
                    <input
                      type={showStripeKeys ? "text" : "password"}
                      value={stripeForm.webhook_secret}
                      onChange={(e) => setStripeForm({ ...stripeForm, webhook_secret: e.target.value })}
                      placeholder="whsec_..."
                      className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                      data-testid="stripe-webhook-secret-input"
                    />
                  </div>
                  
                  <button
                    onClick={() => setShowStripeKeys(!showStripeKeys)}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    {showStripeKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showStripeKeys ? 'Hide' : 'Show'} keys
                  </button>
                </div>
                
                <Button
                  onClick={connectStripe}
                  disabled={stripeConnecting}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  data-testid="stripe-connect-btn"
                >
                  {stripeConnecting ? 'Connecting...' : 'Connect Stripe'}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Connection Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Source:</span>
                      <span className="font-medium">{stripeConfig.source}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">API Key:</span>
                      <span className="font-mono">{stripeConfig.api_key_masked}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Publishable Key:</span>
                      <span className="font-mono">{stripeConfig.publishable_key_masked}</span>
                    </div>
                  </div>
                </div>
                
                {/* Metrics */}
                {stripeMetrics && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white border rounded-xl p-4">
                      <div className="text-sm text-gray-500 mb-1">Active Subscriptions</div>
                      <div className="text-2xl font-bold text-gray-900">{stripeMetrics.active_subscriptions}</div>
                    </div>
                    <div className="bg-white border rounded-xl p-4">
                      <div className="text-sm text-gray-500 mb-1">MRR</div>
                      <div className="text-2xl font-bold text-gray-900">${stripeMetrics.mrr.toFixed(2)}</div>
                    </div>
                    <div className="bg-white border rounded-xl p-4">
                      <div className="text-sm text-gray-500 mb-1">Recent Payments</div>
                      <div className="text-2xl font-bold text-green-600">{stripeMetrics.recent_payments_count}</div>
                    </div>
                    <div className="bg-white border rounded-xl p-4">
                      <div className="text-sm text-gray-500 mb-1">Failed Payments</div>
                      <div className="text-2xl font-bold text-red-600">{stripeMetrics.failed_payments_count}</div>
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={testStripe}
                    disabled={loading}
                    variant="outline"
                    data-testid="stripe-test-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Test Connection
                  </Button>
                  <Button
                    onClick={syncStripeData}
                    disabled={loading}
                    variant="outline"
                    data-testid="stripe-sync-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sync Data
                  </Button>
                  <Button
                    onClick={() => {
                      setStripeForm({
                        api_key: '',
                        publishable_key: '',
                        webhook_secret: ''
                      });
                      setStripeConfig(null);
                      setShowStripeKeys(false);
                    }}
                    variant="outline"
                    data-testid="stripe-edit-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {stripeConfig.source === 'environment' ? 'Update Keys' : 'Edit Keys'}
                  </Button>
                  {stripeConfig.source === 'database' && (
                    <Button
                      onClick={disconnectStripe}
                      disabled={loading}
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:border-red-300"
                      data-testid="stripe-delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
      
      {/* SendGrid Section */}
      {activeSection === 'sendgrid' && (
        <div className="space-y-6">
          <Card className="p-6" data-testid="sendgrid-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">SendGrid Integration</h3>
                  <p className="text-sm text-gray-500">Email delivery service</p>
                </div>
              </div>
              
              {sendgridConfig && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-green-50 text-green-700">
                  <Check className="w-4 h-4" />
                  Connected
                </span>
              )}
            </div>
            
            {!sendgridConfig ? (
              <div className="space-y-4">
                <p className="text-gray-600">Connect SendGrid to send transactional emails.</p>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      API Key
                    </label>
                    <input
                      type={showSendgridKey ? "text" : "password"}
                      value={sendgridForm.api_key}
                      onChange={(e) => setSendgridForm({ ...sendgridForm, api_key: e.target.value })}
                      placeholder="SG...."
                      className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-emerald-500"
                      data-testid="sendgrid-api-key-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sender Email
                    </label>
                    <input
                      type="email"
                      value={sendgridForm.sender_email}
                      onChange={(e) => setSendgridForm({ ...sendgridForm, sender_email: e.target.value })}
                      placeholder="notifications@yourdomain.com"
                      className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-emerald-500"
                      data-testid="sendgrid-sender-email-input"
                    />
                  </div>
                  
                  <button
                    onClick={() => setShowSendgridKey(!showSendgridKey)}
                    className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                  >
                    {showSendgridKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {showSendgridKey ? 'Hide' : 'Show'} key
                  </button>
                </div>
                
                <Button
                  onClick={connectSendgrid}
                  disabled={sendgridConnecting}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                  data-testid="sendgrid-connect-btn"
                >
                  {sendgridConnecting ? 'Connecting...' : 'Connect SendGrid'}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Connection Info */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Source:</span>
                      <span className="font-medium">{sendgridConfig.source}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">API Key:</span>
                      <span className="font-mono">{sendgridConfig.api_key_masked}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sender Email:</span>
                      <span className="font-medium">{sendgridConfig.sender_email}</span>
                    </div>
                  </div>
                </div>
                
                {/* Metrics */}
                {sendgridMetrics && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-white border rounded-xl p-4">
                      <div className="text-sm text-gray-500 mb-1">Sends (24h)</div>
                      <div className="text-2xl font-bold text-gray-900">{sendgridMetrics.sends_24h}</div>
                    </div>
                    <div className="bg-white border rounded-xl p-4">
                      <div className="text-sm text-gray-500 mb-1">Bounce Rate</div>
                      <div className="text-2xl font-bold text-orange-600">{sendgridMetrics.bounce_rate}%</div>
                    </div>
                    <div className="bg-white border rounded-xl p-4">
                      <div className="text-sm text-gray-500 mb-1">Spam Rate</div>
                      <div className="text-2xl font-bold text-red-600">{sendgridMetrics.spam_complaint_rate}%</div>
                    </div>
                  </div>
                )}
                
                {/* Test Email */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Send Test Email
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={testEmailRecipient}
                      onChange={(e) => setTestEmailRecipient(e.target.value)}
                      placeholder="recipient@example.com"
                      className="flex-1 px-4 py-2 border rounded-xl focus:ring-2 focus:ring-emerald-500"
                      data-testid="test-email-recipient-input"
                    />
                    <Button
                      onClick={sendTestEmail}
                      disabled={loading}
                      data-testid="send-test-email-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send
                    </Button>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={testSendgrid}
                    disabled={loading}
                    variant="outline"
                    data-testid="sendgrid-test-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Test Connection
                  </Button>
                  <Button
                    onClick={() => {
                      setSendgridForm({
                        api_key: '',
                        sender_email: ''
                      });
                      setSendgridConfig(null);
                      setShowSendgridKey(false);
                    }}
                    variant="outline"
                    data-testid="sendgrid-edit-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {sendgridConfig.source === 'environment' ? 'Update Keys' : 'Edit Keys'}
                  </Button>
                  {sendgridConfig.source === 'database' && (
                    <Button
                      onClick={disconnectSendgrid}
                      disabled={loading}
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:border-red-300"
                      data-testid="sendgrid-delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
      
      {/* Webhooks Section */}
      {activeSection === 'webhooks' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Webhook Endpoints</h3>
              <p className="text-sm text-gray-500">Send events to external services</p>
            </div>
            <Button
              onClick={() => {
                setShowWebhookModal(true);
                setEditingWebhook(null);
                setWebhookForm({
                  name: '',
                  url: '',
                  secret: '',
                  events: [],
                  headers: {},
                  enabled: true
                });
              }}
              data-testid="create-webhook-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Webhook
            </Button>
          </div>
          
          {/* Webhooks List */}
          <div className="space-y-4">
            {webhooks.length === 0 ? (
              <Card className="p-8 text-center">
                <Webhook className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No webhooks configured yet</p>
              </Card>
            ) : (
              webhooks.map((webhook) => (
                <Card key={webhook.id} className="p-6" data-testid={`webhook-${webhook.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-bold text-gray-900">{webhook.name}</h4>
                        {webhook.enabled ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Disabled
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{webhook.url}</p>
                      <div className="flex flex-wrap gap-2">
                        {webhook.events.map((event) => (
                          <span key={event} className="px-2 py-1 rounded-lg text-xs font-medium bg-purple-50 text-purple-700">
                            {event}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => fetchDeliveryLogs(webhook.id)}
                        variant="outline"
                        size="sm"
                        data-testid={`webhook-logs-btn-${webhook.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => {
                          setEditingWebhook(webhook);
                          setWebhookForm({
                            name: webhook.name,
                            url: webhook.url,
                            secret: '',
                            events: webhook.events,
                            headers: webhook.headers || {},
                            enabled: webhook.enabled
                          });
                          setShowWebhookModal(true);
                        }}
                        variant="outline"
                        size="sm"
                        data-testid={`webhook-edit-btn-${webhook.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => deleteWebhook(webhook.id)}
                        variant="outline"
                        size="sm"
                        data-testid={`webhook-delete-btn-${webhook.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex gap-6 text-sm">
                    <div>
                      <span className="text-gray-500">Total:</span>
                      <span className="ml-1 font-medium">{webhook.total_deliveries || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Success:</span>
                      <span className="ml-1 font-medium text-green-600">{webhook.successful_deliveries || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Failed:</span>
                      <span className="ml-1 font-medium text-red-600">{webhook.failed_deliveries || 0}</span>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      )}
      
      {/* Webhook Modal */}
      {showWebhookModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowWebhookModal(false)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingWebhook ? 'Edit Webhook' : 'Create Webhook'}
                </h3>
                <button onClick={() => setShowWebhookModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={webhookForm.name}
                    onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
                    placeholder="GoHighLevel CRM"
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500"
                    data-testid="webhook-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                  <input
                    type="url"
                    value={webhookForm.url}
                    onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                    placeholder="https://api.yourdomain.com/webhook"
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500"
                    data-testid="webhook-url-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secret</label>
                  <input
                    type="password"
                    value={webhookForm.secret}
                    onChange={(e) => setWebhookForm({ ...webhookForm, secret: e.target.value })}
                    placeholder={editingWebhook ? "Leave empty to keep existing" : "Enter a secret"}
                    className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500"
                    data-testid="webhook-secret-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subscribe to Events</label>
                  <div className="max-h-64 overflow-y-auto border rounded-xl p-4 space-y-2">
                    {availableEvents.map((event) => (
                      <label key={event.id} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={webhookForm.events.includes(event.id)}
                          onChange={() => toggleEventSelection(event.id)}
                          className="mt-1"
                          data-testid={`event-checkbox-${event.id}`}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{event.id}</div>
                          <div className="text-sm text-gray-500">{event.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  onClick={() => setShowWebhookModal(false)}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createOrUpdateWebhook}
                  disabled={loading}
                  data-testid="webhook-save-btn"
                >
                  {loading ? 'Saving...' : (editingWebhook ? 'Update' : 'Create')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
      
      {/* Delivery Logs Modal */}
      {showDeliveryLogs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDeliveryLogs(false)}>
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Delivery Logs</h3>
                <button onClick={() => setShowDeliveryLogs(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-3">
                {deliveryLogs.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No delivery logs yet</p>
                ) : (
                  deliveryLogs.map((log) => (
                    <div key={log.id} className="border rounded-xl p-4" data-testid={`delivery-log-${log.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-medium text-gray-900">{log.event_type}</div>
                          <div className="text-sm text-gray-500">
                            {new Date(log.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {log.status === 'success' ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                              Success ({log.status_code})
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                              Failed ({log.status_code || 'N/A'})
                            </span>
                          )}
                          {log.status === 'failed' && (
                            <Button
                              onClick={() => retryDelivery(log.id)}
                              size="sm"
                              variant="outline"
                              data-testid={`retry-delivery-btn-${log.id}`}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              Retry
                            </Button>
                          )}
                        </div>
                      </div>
                      {log.response_ms && (
                        <div className="text-sm text-gray-500">Response time: {log.response_ms}ms</div>
                      )}
                      {log.error_message && (
                        <div className="text-sm text-red-600 mt-2">Error: {log.error_message}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default IntegrationsTab;
